import { useState, useCallback } from "react";
import { backend } from "@/services/backend";
import { useTelegram } from "@/hooks/useTelegram";
import { useSnackbar } from "notistack";

interface UseStarPaymentOptions {
  onSuccess?: () => void | Promise<void>;
}

interface UseStarPaymentReturn {
  sendStars: (address: string, starCount: number) => Promise<boolean>;
  isLoading: boolean;
}

export function useStarPayment(
  options: UseStarPaymentOptions = {}
): UseStarPaymentReturn {
  const { onSuccess } = options;
  const { user: telegramUser, isAuthenticated: isTelegramAuthenticated } =
    useTelegram();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const sendStars = useCallback(
    async (address: string, starCount: number): Promise<boolean> => {
      if (!window.Telegram?.WebApp) {
        enqueueSnackbar("This feature is only available in Telegram!", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 3000,
        });
        return false;
      }

      if (!isTelegramAuthenticated || !telegramUser?.id) {
        enqueueSnackbar("Please authenticate with Telegram first!", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 3000,
        });
        return false;
      }

      setIsLoading(true);

      try {
        const result = await backend.initStarPayment({
          chatId: telegramUser.id,
          address,
          count: starCount
        });

        if (result.success) {
          enqueueSnackbar("Telegram Stars sent successfully!", {
            SnackbarProps: {
              onClick: () => {
                closeSnackbar();
              },
            },
            variant: "success",
            autoHideDuration: 2000,
          });

          if (onSuccess) {
            await onSuccess();
          }

          setIsLoading(false);
          return true;
        } else {
          enqueueSnackbar("Operation failed. Please try again.", {
            SnackbarProps: {
              onClick: () => {
                closeSnackbar();
              },
            },
            variant: "error",
            autoHideDuration: 3000,
          });
          setIsLoading(false);
          return false;
        }
      } catch (error) {
        console.error("Error sending stars:", error);
        enqueueSnackbar("An error occurred. Please try again.", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 3000,
        });
        setIsLoading(false);
        return false;
      }
    },
    [telegramUser, isTelegramAuthenticated, enqueueSnackbar, onSuccess]
  );

  return {
    sendStars,
    isLoading,
  };
}
